import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";

const agents = [
  {
    name: "Amanda",
    role: "Insurance Verification",
    description: "Automatically verifies patient insurance benefits and eligibility hours before visits.",
    image: "/assets/amanda.png",
    skills: ["Verification", "Benefits"],
  },
  {
    name: "Ariel",
    role: "Claims Expert",
    description: "Generates and submits clean insurance claims to payers instantly.",
    image: "/assets/ariel.png",
    skills: ["Claims", "Submission"],
  },
  {
    name: "Alayna",
    role: "Claim Scrubbing",
    description: "Reviews and corrects claims before submission to reduce denials.",
    image: "https://framerusercontent.com/images/gpvxkPXtZHSDq7LbDrvpyEwKzAk.png",
    skills: ["Auditing", "Corrections"],
  },
  {
    name: "Sarah",
    role: "Denial Management",
    description: "Handles denied claims, appeals, and resubmissions automatically.",
    image: "/assets/sarah.png",
    skills: ["Appeals", "Resubmission"],
  },
  {
    name: "Kianna",
    role: "Patient Billing",
    description: "Manages patient statements and answers billing questions via text/phone.",
    image: "/assets/kianna.png",
    skills: ["Statements", "Payments"],
  },
  {
    name: "Julia",
    role: "AR & Collections",
    description: "Tracks accounts receivable and manages collection activities.",
    image: "/assets/julia.png",
    skills: ["Collections", "Tracking"],
  },
  {
    name: "Alisha",
    role: "EOB Posting",
    description: "Posts explanation of benefits and insurance payments automatically.",
    image: "/assets/alisha.png",
    skills: ["Posting", "Reconciliation"],
  },
  {
    name: "Laura",
    role: "Insurance Calls",
    description: "Handles insurance-related phone calls and inquiries efficiently.",
    image: "https://framerusercontent.com/images/8s85NGEzWU5LVKALaMlopNKSHg.png",
    skills: ["Inbound Calls", "Inquiries"],
  },
];

const slugMap = {
  Amanda: "amanda",
  Ariel: "ariel",
  Alisha: "alisha",
  Kianna: "kianna",
};

const PMSTeamIntro2Slide = ({ onAgentClick }) => {
  return (
    <div className="h-full w-full bg-slate-100 flex flex-col p-8">
      {/* Header */}
      <div className="text-center mb-6 flex-shrink-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-3">
            <Sparkles className="w-4 h-4" />
            <span>Digital Workforce</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
            8 AI Agents to Run Your{" "}
            <span className="text-blue-600">Revenue Cycle</span>.
          </h2>
          <p className="text-sm md:text-base text-slate-500 max-w-2xl mx-auto mt-2">
            From insurance verification to claims, EOB posting, patient billing, and collections — fully automated.
          </p>
        </motion.div>
      </div>

      {/* Agent Cards Grid */}
      <div className="flex-grow flex items-center justify-center overflow-hidden">
        <div className="grid grid-cols-4 gap-4 w-full max-w-[1300px]">
          {agents.map((agent, index) => (
            <motion.div
              key={agent.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="h-full"
            >
              <div
                className="group/card relative bg-white rounded-xl border border-slate-200 hover:border-blue-200 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col cursor-pointer h-[240px]"
                onClick={() => onAgentClick?.(slugMap[agent.name])}
              >
                {/* Agent Header */}
                <div className="p-4 pb-0 flex items-start justify-between">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-xl overflow-hidden shadow-sm ring-1 ring-slate-100">
                      <img
                        src={agent.image}
                        alt={agent.name}
                        className="w-full h-full object-cover transform group-hover/card:scale-110 transition-transform duration-500"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.parentNode.style.backgroundColor = "#f1f5f9";
                        }}
                      />
                    </div>
                    <div
                      className="absolute -bottom-1 -right-1 bg-green-500 border-[2px] border-white w-4 h-4 rounded-full"
                      title="Online"
                    />
                  </div>
                  <div className="w-6 h-6 rounded-full bg-slate-50 group-hover/card:bg-blue-600 flex items-center justify-center transition-colors duration-300">
                    <ArrowRight className="w-3 h-3 text-slate-400 group-hover/card:text-white transition-colors" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 pt-3 flex-1 flex flex-col min-h-0">
                  <div className="mb-2">
                    <h3 className="text-lg font-bold text-slate-900 leading-tight">
                      {agent.name},{" "}
                      <span className="text-blue-600 font-bold">
                        {agent.role}
                      </span>
                    </h3>
                  </div>

                  <p className="text-slate-600 text-xs leading-snug mb-3 line-clamp-3">
                    {agent.description}
                  </p>

                  {/* Skills Tags */}
                  <div className="flex flex-wrap gap-1 mt-auto">
                    {agent.skills.slice(0, 2).map((skill, i) => (
                      <span
                        key={i}
                        className="px-1.5 py-0.5 rounded-md bg-slate-50 text-slate-600 text-[9px] font-bold border border-slate-100 uppercase tracking-wider"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Hover highlight bar */}
                <div className="h-1 w-full bg-gradient-to-r from-blue-500 to-indigo-500 transform scale-x-0 group-hover/card:scale-x-100 transition-transform duration-300 origin-left" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PMSTeamIntro2Slide;
